import { Loader, Tag, VText } from '@vake/ui-kit';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useGetVesselTagsQuery } from '../../../requests/actor-mode';
import { useSendToMattermost } from '../../../requests/reports';
import { labelToDot } from '../../../shared/components/object-class-dot';
import { formatRelativeTime } from '../../../shared/components/time';
import { mmsiToFlag } from '../../../utils/mmsi-to-flag';
import { TagView } from './tags';

type Props = {
    row: ActorModeVessel;
    reportUuid: string;
    onClick: (insightUuid: string) => void;
    filterTags: Tag[];
    tagFilterStrategy: string;
};
export const ActorModeRow: FC<Props> = ({ row, reportUuid, onClick, filterTags, tagFilterStrategy }) => {
    const {
        data: tags,
        isLoading: tagsLoading,
        isError: tagsError,
        isSuccess: tagsSuccess,
    } = useGetVesselTagsQuery(row.uuid);
    const { mutate } = useSendToMattermost();

    const TagInRowView = () => {
        if (tagsLoading) {
            return (
                <div className="flex justify-center">
                    <Loader size={16} />
                </div>
            );
        }
        if (tagsError) {
            mutate(`In Actor Mode row: Error loading actions for insight ${row.uuid}`);
            return <VText>Error</VText>;
        }
        if (!tags || tags.length === 0) {
            return <></>;
        }
        return <TagView tags={tags} />;
    };
    const shouldViewRow = (): boolean => {
        const activeTags = filterTags.filter((tag) => !tag.subtle);
        if (activeTags.length === 0) {
            return true;
        }
        const selectedTags = activeTags.map((tag) => tag.children as string);
        if (tags && tagFilterStrategy === 'or') {
            return selectedTags.some((tag) => tags.includes(tag));
        } else if (tags && tagFilterStrategy === 'and') {
            return selectedTags.every((tag) => tags.includes(tag));
        }
        return true;
    };
    if (tagsSuccess && !shouldViewRow()) {
        return <></>;
    }
    return (
        <div className="hover:bg-background-primary-hover p-2 items-center">
            <Link
                to={`/actor/${reportUuid}/vessel/${row.uuid}`}
                className="grid grid-cols-7"
                key={row.uuid}
                onClick={() => onClick(row.uuid)}
            >
                {labelToDot(row.matchingLabel)}
                <VText variant="B2">{row.name}</VText>
                <VText variant="B2">{row.imo}</VText>
                <div className="flex items-start gap-2">
                    <VText>{mmsiToFlag(row.mmsi)}</VText>
                    <VText variant="B2">{row.mmsi}</VText>
                </div>
                <VText variant="B2">{formatRelativeTime(new Date(row.lastUpdated))}</VText>
                <VText variant="B2">{row.lastPosition}</VText>
                <div className="flex flex-col gap-1">
                    <TagInRowView />
                </div>
            </Link>
        </div>
    );
};
